import React from "react";

/* =========================================================
   src/lib.jsx - Icon (lucide), Deck engine, Cursor
   ========================================================= */

/* ---------- Lucide icon ----------------------------------------------- */
const _kebabToCamel = (s) => s.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
function _camelAttrs(attrs) {
  const out = {};
  for (const k in attrs) {
    if (k === "class") out.className = attrs[k];
    else out[_kebabToCamel(k)] = attrs[k];
  }
  return out;
}
function Icon({ name, size = 18, strokeWidth = 2, className, ...rest }) {
  const def = window.lucide && window.lucide[name];
  if (!def) {
    return <span style={{ display: "inline-block", width: size, height: size }} aria-hidden="true" />;
  }
  const [, attrs, children] = def;
  return (
    <svg
      {..._camelAttrs(attrs)}
      width={size}
      height={size}
      strokeWidth={strokeWidth}
      className={className}
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children.map(([tag, props], i) => React.createElement(tag, { ..._camelAttrs(props), key: i }))}
    </svg>
  );
}

/* ---------- math ------------------------------------------------------- */
function clamp01(v) { return Math.max(0, Math.min(1, v)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function smoothstep(t) { const x = clamp01(t); return x * x * (3 - 2 * x); }
function lerpRgb(a, b, t) {
  return [
    Math.round(lerp(a[0], b[0], t)),
    Math.round(lerp(a[1], b[1], t)),
    Math.round(lerp(a[2], b[2], t)),
  ];
}
function luminance([r, g, b]) {
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}
function isReducedMotion() {
  return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}
function isTouchOrSmall() {
  if (!window.matchMedia) return false;
  return (
    window.matchMedia("(max-width: 1023px)").matches ||
    window.matchMedia("(max-height: 699px)").matches ||
    window.matchMedia("(hover: none)").matches
  );
}
function usesFlowFallback() {
  if (!window.matchMedia) return false;
  return window.matchMedia("(max-height: 699px) and (min-width: 721px)").matches;
}
function useMediaQuery(query) {
  const getMatches = () => (
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia(query).matches
  );
  const [matches, setMatches] = React.useState(getMatches);

  React.useEffect(() => {
    if (!window.matchMedia) return undefined;
    const media = window.matchMedia(query);
    const onChange = () => setMatches(media.matches);
    onChange();
    media.addEventListener?.("change", onChange);
    media.addListener?.(onChange);
    return () => {
      media.removeEventListener?.("change", onChange);
      media.removeListener?.(onChange);
    };
  }, [query]);

  return matches;
}

/* ---------- BG palette — ocean-depth progression ---------------------- */
const BG_PALETTE = {
  paper:  [22, 34, 64],   // hero: ocean surface — deep navy blue
  paper2: [13, 18, 42],   // services: mid-depth
  ink:    [8,  10, 22],   // values + contact: deep ocean, near-black
};
function bgRgb(id) { return BG_PALETTE[id] || BG_PALETTE.paper; }

/* ---------- Deck + Slide ---------------------------------------------- */
/* The deck is one tall container. Inside, a sticky <.deck-pin> holds all
   slides absolute-stacked. As the user scrolls 0 .. (N-1)*100vh, slides
   crossfade. After the last slide reaches full visibility, the deck unpins
   and normal flow continues (e.g. a tall footer or extra footer below). */

function Deck({ slides, footer }) {
  // slides: [{id, bg, name, content}]; footer: rendered AFTER the deck (and
  // also rendered inline as part of the last slide via Contact).
  const ref = React.useRef(null);
  const N = slides.length;
  const heightCss = `calc(${N} * 100svh)`; // see useDeckEngine for scroll math

  return (
    <>
      <div ref={ref} className="deck" style={{ height: heightCss }}>
        <div className="deck-pin">
          {slides.map((s, i) => (
            <section
              key={s.id}
              id={s.id}
              className="slide"
              data-bg={s.bg}
              data-idx={i}
              data-screen-label={s.name || s.id}
              aria-hidden={i === 0 ? "false" : "true"}
            >
              <div className="slide-content">{s.content}</div>
            </section>
          ))}
        </div>
      </div>
      {footer}
    </>
  );
}

/* useDeckEngine: per-frame opacity + transform + bg color, plus mounts a
   "go to slide" helper on window. */
function useDeckEngine(enabled = true) {
  React.useEffect(() => {
    if (!enabled) return undefined;

    if (isReducedMotion() || usesFlowFallback()) {
      // Disable engine - let CSS show all slides normally.
      window.__deckGoTo = (id) => {
        const el = document.getElementById(id);
        if (!el) return;
        const navH = 72;
        const targetY = el.getBoundingClientRect().top + window.scrollY - navH - 12;
        animateScrollTo(Math.max(0, targetY), 420);
      };
      return;
    }

    const bgLayer = document.querySelector(".bg-layer");
    const body = document.body;
    let raf;
    let lastBgClass = "";

    function tick() {
      raf = null;
      const slides = document.querySelectorAll(".slide");
      if (!slides.length) return;
      const N = slides.length;
      const vh = window.innerHeight || document.documentElement.clientHeight;

      // Deck pin range = (N - 1) * vh of scroll
      const deckRange = Math.max(1, (N - 1) * vh);
      const t = clamp01(window.scrollY / deckRange);
      const sectionProg = t * (N - 1);
      const activeIdx = Math.round(sectionProg);

      slides.forEach((el, i) => {
        const d = sectionProg - i;       // signed distance
        const absD = Math.abs(d);
        const op = Math.max(0, 1 - absD); // linear ramp, 1 at center, 0 at ±1
        const eased = op * op * (3 - 2 * op);
        const ty = d * 28;               // pushes slide upward as we leave it; below as we approach
        el.style.opacity = eased.toFixed(3);
        el.style.transform = `translate3d(0, ${ty.toFixed(1)}px, 0)`;
        const isActive = i === activeIdx;
        el.classList.toggle("is-active", isActive);
        el.setAttribute("aria-hidden", isActive ? "false" : "true");
      });

      // Background crossfade
      const lo = Math.floor(sectionProg);
      const hi = Math.min(N - 1, lo + 1);
      const sub = clamp01(sectionProg - lo);
      const a = bgRgb(slides[lo].dataset.bg);
      const b = bgRgb(slides[hi].dataset.bg);
      const c = lerpRgb(a, b, smoothstep(sub));
      if (bgLayer) bgLayer.style.background = `rgb(${c[0]}, ${c[1]}, ${c[2]})`;

      // Body bg-dark flag
      const dark = luminance(c) < 0.45;
      const cls = dark ? "bg-dark" : "bg-light";
      if (cls !== lastBgClass) {
        body.classList.toggle("bg-dark", dark);
        body.classList.toggle("bg-light", !dark);
        lastBgClass = cls;
      }
    }
    function onScroll() { if (!raf) raf = requestAnimationFrame(tick); }
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    tick();

    // Programmatic navigation: snap-fast scroll to slide
    window.__deckGoTo = (id) => {
      const slides = document.querySelectorAll(".slide");
      const idx = [...slides].findIndex((el) => el.id === id);
      if (idx < 0) return;
      const vh = window.innerHeight;
      const targetY = idx * vh;
      animateScrollTo(targetY, isTouchOrSmall() ? 440 : 380);
    };

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
      delete window.__deckGoTo;
    };
  }, [enabled]);
}

/* Custom-easing scroll animation. Default ~350ms for snappy snap. */
let _scrollRaf = null;
let _scrollEndAt = 0;
function isScrollAnimating() { return _scrollRaf !== null; }
function animateScrollTo(targetY, duration = 350, onDone) {
  if (_scrollRaf) cancelAnimationFrame(_scrollRaf);
  const startY = window.scrollY;
  const dist = targetY - startY;
  if (Math.abs(dist) < 1) { onDone && onDone(); return; }
  const startTime = performance.now();
  function step(now) {
    const elapsed = now - startTime;
    const k = Math.min(1, elapsed / duration);
    // ease-out cubic - fast start, gentle finish
    const eased = 1 - Math.pow(1 - k, 3);
    window.scrollTo(0, startY + dist * eased);
    if (k < 1) _scrollRaf = requestAnimationFrame(step);
    else { _scrollRaf = null; _scrollEndAt = performance.now(); onDone && onDone(); }
  }
  _scrollRaf = requestAnimationFrame(step);
}

function smoothScrollTo(id) {
  if (typeof window.__deckGoTo === "function") {
    window.__deckGoTo(id);
  } else {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

/* ---------- Snap controller (wheel, touch, keyboard) ------------------ */
/* Hijacks scroll input and animates between slide-aligned scrollY values.
   One wheel notch / arrow key / swipe = one slide. */
function useSnapController(enabled = true) {
  React.useEffect(() => {
    if (!enabled) return;
    if (isReducedMotion() || usesFlowFallback()) return;

    if (isTouchOrSmall()) {
      let restTimer = null;
      const snapToNearest = () => {
        if (isScrollAnimating()) return;
        clearTimeout(restTimer);
        restTimer = setTimeout(() => {
          if (isScrollAnimating()) return;
          const slides = document.querySelectorAll(".slide");
          if (!slides.length) return;
          const vh = window.innerHeight || document.documentElement.clientHeight;
          const last = slides.length - 1;
          const target = Math.max(0, Math.min(last, Math.round(window.scrollY / vh)));
          const targetY = target * vh;
          if (Math.abs(window.scrollY - targetY) > 18) {
            animateScrollTo(targetY, 280);
          }
        }, 120);
      };
      window.addEventListener("scroll", snapToNearest, { passive: true });
      return () => {
        window.removeEventListener("scroll", snapToNearest);
        clearTimeout(restTimer);
      };
    }

    const cooldownMs = 120;

    function reEvalAndMaybeBail() {
      // Bail out entirely if env changed (e.g. window resized to mobile,
      // or hover suddenly registered as none).
      return isReducedMotion() || isTouchOrSmall();
    }

    function currentIndex() {
      const vh = window.innerHeight;
      return Math.round(window.scrollY / vh);
    }
    function lastIndex() {
      return document.querySelectorAll(".slide").length - 1;
    }
    function step(dir) {
      const cur = currentIndex();
      const next = Math.max(0, Math.min(lastIndex(), cur + dir));
      if (next === cur) return false;
      animateScrollTo(next * window.innerHeight, 380);
      return true;
    }

    function shouldIgnore(target) {
      if (!target || !target.closest) return false;
      return !!target.closest("textarea, input, [data-allow-scroll]");
    }

    function onWheel(e) {
      if (reEvalAndMaybeBail()) return;
      if (shouldIgnore(e.target)) return;
      if (Math.abs(e.deltaY) < 5) { e.preventDefault(); return; }
      if (isScrollAnimating() || performance.now() - _scrollEndAt < cooldownMs) {
        e.preventDefault();
        return;
      }
      const dir = e.deltaY > 0 ? 1 : -1;
      e.preventDefault();
      step(dir);
    }

    function onKey(e) {
      if (reEvalAndMaybeBail()) return;
      const tag = (e.target && e.target.tagName) || "";
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable) return;
      let dir = 0;
      if (["ArrowDown", "PageDown", " "].includes(e.key)) dir = 1;
      else if (["ArrowUp", "PageUp"].includes(e.key)) dir = -1;
      else if (e.key === "Home") { e.preventDefault(); animateScrollTo(0, 380); return; }
      else if (e.key === "End") { e.preventDefault(); animateScrollTo(lastIndex() * window.innerHeight, 380); return; }
      if (!dir) return;
      if (isScrollAnimating()) { e.preventDefault(); return; }
      e.preventDefault();
      step(dir);
    }

    window.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("keydown", onKey);

    // Defensive: if scroll comes to rest between slides, snap to nearest.
    // Desktop-only - mobile uses native scroll without this safety net.
    let restTimer = null;
    function onScroll() {
      if (reEvalAndMaybeBail() || isScrollAnimating()) return;
      clearTimeout(restTimer);
      restTimer = setTimeout(() => {
        if (reEvalAndMaybeBail() || isScrollAnimating()) return;
        const vh = window.innerHeight;
        const cur = window.scrollY / vh;
        const target = Math.round(cur);
        if (Math.abs(cur - target) > 0.015) {
          animateScrollTo(target * vh, 260);
        }
      }, 140);
    }
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll);
      clearTimeout(restTimer);
    };
  }, [enabled]);
}

/* ---------- scroll progress -------------------------------------------- */
function useScrollY() {
  const [y, setY] = React.useState(0);
  React.useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setY(window.scrollY);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    setY(window.scrollY);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return y;
}
function useScrollProgressTotal() {
  const y = useScrollY();
  const [max, setMax] = React.useState(1);
  React.useEffect(() => {
    const compute = () => setMax(Math.max(1, document.documentElement.scrollHeight - window.innerHeight));
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);
  return Math.min(1, Math.max(0, y / max));
}

function useActiveSlide() {
  const [idx, setIdx] = React.useState(0);
  React.useEffect(() => {
    let raf;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        const slides = document.querySelectorAll(".slide");
        if (!slides.length) return;
        const N = slides.length;
        const vh = window.innerHeight;
        const t = clamp01(window.scrollY / ((N - 1) * vh));
        setIdx(Math.round(t * (N - 1)));
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return idx;
}

/* ---------- Cursor glow + dot ----------------------------------------- */
function CursorGlow() {
  const glowRef = React.useRef(null);
  const dotRef = React.useRef(null);
  const target = React.useRef({ x: -9999, y: -9999 });
  const pos = React.useRef({ x: -9999, y: -9999 });

  React.useEffect(() => {
    if (window.matchMedia && window.matchMedia("(hover: none)").matches) return;
    document.documentElement.classList.add("has-custom-cursor");
    let glowHalf = 260;

    const updateGlowSize = () => {
      if (!glowRef.current) return;
      glowHalf = glowRef.current.offsetWidth / 2 || 260;
    };
    updateGlowSize();

    const onMove = (e) => {
      target.current.x = e.clientX;
      target.current.y = e.clientY;
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const hoverable = el && el.closest && el.closest("a, button, .btn, input, textarea, [data-hover]");
      if (dotRef.current) dotRef.current.classList.toggle("is-hover", !!hoverable);
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("resize", updateGlowSize);

    let raf;
    const loop = () => {
      pos.current.x += (target.current.x - pos.current.x) * 0.22;
      pos.current.y += (target.current.y - pos.current.y) * 0.22;
      if (glowRef.current) {
        glowRef.current.style.transform =
          `translate3d(${pos.current.x - glowHalf}px, ${pos.current.y - glowHalf}px, 0)`;
      }
      if (dotRef.current) {
        const half = dotRef.current.classList.contains("is-hover") ? 14 : 4;
        dotRef.current.style.transform =
          `translate3d(${pos.current.x - half}px, ${pos.current.y - half}px, 0)`;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    const onLeave = () => {
      target.current.x = -9999;
      target.current.y = -9999;
    };
    document.addEventListener("mouseleave", onLeave);

    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("resize", updateGlowSize);
      document.removeEventListener("mouseleave", onLeave);
      cancelAnimationFrame(raf);
      document.documentElement.classList.remove("has-custom-cursor");
    };
  }, []);

  return (
    <>
      <div ref={glowRef} className="cursor-glow" aria-hidden="true" />
      <div ref={dotRef} className="cursor-dot" aria-hidden="true" />
    </>
  );
}

function BackgroundLayer() {
  return (
    <>
      <div className="bg-layer" aria-hidden="true" />
      <div className="bg-noise" aria-hidden="true" />
    </>
  );
}
function ScrollProgressBar() {
  const p = useScrollProgressTotal();
  return (
    <div className="progress" style={{ transform: `scaleX(${p})` }} />
  );
}

/* On mount: disable scroll restoration and force scroll to top */
function useScrollToTopOnMount() {
  React.useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    window.scrollTo(0, 0);
  }, []);
}

Object.assign(window, {
  Icon, Deck,
  useDeckEngine, useActiveSlide, useScrollToTopOnMount, useSnapController, useMediaQuery,
  useScrollY, useScrollProgressTotal,
  smoothScrollTo, animateScrollTo, isReducedMotion,
  CursorGlow, BackgroundLayer, ScrollProgressBar,
});
